import React, { useMemo, useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Layer, WidgetSettings, VideoSettings } from '../../types/design';
import { TextStyle } from '../../pages/template-editor/textEditorTypes';
import { BackgroundSettings } from './BackgroundPropertiesPanel';
import { ImageSettings } from './ImagePropertiesPanel';
import { ShapeSettings } from './ShapePropertiesPanel';
import { Scroller } from './Scroller';
import { useInterpolatedAnimation } from '../../hooks/useInterpolatedAnimation';
import IFrameComponent from '../shared/IFrameComponent';
import { getWidgetInteractionKey } from '../../utils/widgetUtils';
import { Loader2 } from 'lucide-react';
import { PlaylistRenderer } from './PlaylistRenderer';

// --- Shared Helper Components ---

export const getImageVisibleBounds = (
    containerWidth: number,
    containerHeight: number,
    naturalWidth: number,
    naturalHeight: number,
    objectFit: 'cover' | 'contain' | 'fill' = 'contain'
) => {
    if (objectFit === 'fill') {
        return { left: 0, top: 0, width: containerWidth, height: containerHeight };
    }

    const containerRatio = containerWidth / containerHeight;
    const imageRatio = naturalWidth / naturalHeight;

    let width, height, left, top;

    if (objectFit === 'contain') {
        if (containerRatio > imageRatio) {
            height = containerHeight;
            width = height * imageRatio;
            top = 0;
            left = (containerWidth - width) / 2;
        } else {
            width = containerWidth;
            height = width / imageRatio;
            left = 0;
            top = (containerHeight - height) / 2;
        }
    } else { // cover
        if (containerRatio > imageRatio) {
            width = containerWidth;
            height = width / imageRatio;
            left = 0;
            top = (containerHeight - height) / 2;
        } else {
            height = containerHeight;
            width = height * imageRatio;
            top = 0;
            left = (containerWidth - width) / 2;
        }
    }

    return { left, top, width, height };
};

const DEFAULT_SCROLLER_SETTINGS = {
    content: 'SCROLLER TEXT HERE',
    fontFamily: 'Segoe UI',
    fontSize: 24,
    direction: 'rtl' as const,
    textStyle: 'regular' as const,
    scrollSpeed: 'medium' as const,
    textColor: '#FFFFFF',
    backgroundColor: '#002B5B',
    letterSpacing: 0
};

/**
 * Normalizes a URL by removing query strings and hashes to allow stable comparison.
 * Critical for handling S3 Signed URLs where signatures change but the asset is the same.
 */
export const getBaseAssetUrl = (url: string | undefined): string => {
    if (!url) return '';
    try {
        // If it's a platform path or data-uri, return as is
        if (url.startsWith('data:') || url.startsWith('blob:') || !url.includes('http')) return url;
        const parsed = new URL(url);
        return parsed.origin + parsed.pathname;
    } catch (e) {
        return (url || '').split('?')[0].split('#')[0];
    }
};

export const isSameAsset = (url1: string | undefined, url2: string | undefined): boolean => {
    if (!url1 || !url2) return url1 === url2;
    return getBaseAssetUrl(url1) === getBaseAssetUrl(url2);
};

// --- Sub-components for stability (defined OUTSIDE LayerRenderer to prevent remounting) ---

interface SafeImageProps {
    id: string;
    className: string;
    style: React.CSSProperties;
    imgStyle: React.CSSProperties;
    src: string;
    alt: string;
    onLoad?: () => void;
    animationKey: string;
    imageAnimationClass: string;
    imageAnimationStyle: React.CSSProperties;
    onClick?: (e: React.MouseEvent) => void;
    onMouseDown?: (e: React.MouseEvent) => void;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
}

const SafeImage = React.memo(({
    id, className, style, imgStyle, src: incomingSrc, alt, onLoad,
    animationKey, imageAnimationClass, imageAnimationStyle,
    onClick, onMouseDown, onMouseEnter, onMouseLeave
}: SafeImageProps) => {
    // Keep track of the "displayed" source to avoid updating on signature-only changes
    const [displaySrc, setDisplaySrc] = useState(incomingSrc || '/logo.png');
    const [hasError, setHasError] = useState(false);
    const [useCrossOrigin, setUseCrossOrigin] = useState(true);
    const lastBaseUrl = useRef(getBaseAssetUrl(incomingSrc));

    useEffect(() => {
        const newBaseUrl = getBaseAssetUrl(incomingSrc);
        if (newBaseUrl !== lastBaseUrl.current) {
            setDisplaySrc(incomingSrc || '/logo.png');
            setHasError(false);
            setUseCrossOrigin(true);
            lastBaseUrl.current = newBaseUrl;
        }
    }, [incomingSrc]);

    return (
        <motion.div
            id={id}
            key={animationKey}
            className={className}
            style={style}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className={`${imageAnimationClass} w-full h-full`.trim()} style={imageAnimationStyle}>
                <img
                    crossOrigin={useCrossOrigin ? "anonymous" : undefined}
                    src={displaySrc}
                    alt={alt}
                    onLoad={onLoad}
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onError={() => {
                        // First fallback: If CORS blocks the image, try loading without crossOrigin
                        if (useCrossOrigin && displaySrc !== '/logo.png') {
                            setUseCrossOrigin(false);
                            return;
                        }
                        if (hasError) return;
                        setHasError(true);
                        setDisplaySrc('/logo.png');
                    }}
                    style={{
                        ...imgStyle,
                        objectFit: hasError ? 'contain' : (imgStyle.objectFit || 'cover'),
                        padding: hasError ? '20px' : '0',
                        opacity: hasError ? 0.3 : (imgStyle.opacity ?? 1),
                    }}
                />
            </div>
        </motion.div>
    );
});

SafeImage.displayName = 'SafeImage';

interface SafeVideoProps {
    id: string;
    className: string;
    style: React.CSSProperties;
    videoStyle: React.CSSProperties;
    src: string;
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
    animationClass: string;
    animationStyle: React.CSSProperties;
    onClick?: (e: React.MouseEvent) => void;
    onMouseDown?: (e: React.MouseEvent) => void;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
}

const SafeVideo = React.memo(({
    id, className, style, videoStyle, src: incomingSrc,
    autoPlay, loop, muted, animationClass, animationStyle,
    onClick, onMouseDown, onMouseEnter, onMouseLeave
}: SafeVideoProps) => {
    const [displaySrc, setDisplaySrc] = useState(incomingSrc);
    const [isLoading, setIsLoading] = useState(true);
    const lastBaseUrl = useRef(getBaseAssetUrl(incomingSrc));

    useEffect(() => {
        const newBaseUrl = getBaseAssetUrl(incomingSrc);
        if (newBaseUrl !== lastBaseUrl.current) {
            setDisplaySrc(incomingSrc);
            setIsLoading(true);
            lastBaseUrl.current = newBaseUrl;
        }
    }, [incomingSrc]);

    return (
        <motion.div
            id={id}
            className={className}
            style={style}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className={`${animationClass} w-full h-full`.trim()} style={animationStyle}>
                <video
                    src={displaySrc}
                    crossOrigin="anonymous"
                    autoPlay={autoPlay}
                    loop={loop}
                    muted={muted}
                    playsInline
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onWaiting={() => setIsLoading(true)}
                    onPlaying={() => setIsLoading(false)}
                    onCanPlayThrough={() => setIsLoading(false)}
                    onLoadStart={() => setIsLoading(true)}
                    onLoadedMetadata={() => setIsLoading(true)}
                    onLoadedData={() => setIsLoading(false)}
                    style={{
                        ...videoStyle,
                        opacity: isLoading ? 0 : 1,
                    }}
                />
            </div>
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[4px] z-[9999]">
                    <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-10 h-10 text-teal-500 animate-spin shadow-lg" />
                        <span className="text-white text-[10px] font-bold uppercase tracking-widest opacity-80">Loading Video...</span>
                    </div>
                </div>
            )}
        </motion.div>
    );
});

SafeVideo.displayName = 'SafeVideo';

const getTextEffectStyles = (style: any): React.CSSProperties => {
    if (!style?.textEffect || style.textEffect === 'none') return {};

    const intensity = style.effectIntensity ?? 50;
    const color = style.effectColor || '#000000';
    const offset = style.effectOffset ?? 50;
    const direction = style.effectDirection ?? 45;
    const thickness = style.effectThickness ?? 50;

    const rad = (direction * Math.PI) / 180;
    const x = Math.round(Math.cos(rad) * (offset / 10));
    const y = Math.round(Math.sin(rad) * (offset / 10));
    const blur = Math.round(intensity / 10);

    switch (style.textEffect) {
        case 'shadow':
            return { textShadow: `${x}px ${y}px ${blur}px ${color}` };
        case 'lift':
            return { textShadow: `0px ${Math.round(intensity / 5)}px ${Math.round(intensity / 2)}px rgba(0,0,0,0.3)` };
        case 'hollow':
            return {
                WebkitTextStroke: `${Math.round(thickness / 20)}px ${color || style.color || '#000'}`,
                color: 'transparent'
            };
        case 'outline':
            return {
                WebkitTextStroke: `${Math.round(thickness / 20)}px ${color || '#000'}`
            };
        case 'neon':
            return {
                textShadow: `0 0 ${Math.round(intensity / 10)}px ${color}, 0 0 ${Math.round(intensity / 5)}px ${color}, 0 0 ${Math.round(intensity / 2)}px ${color}`
            };
        case 'background':
            return {
                backgroundColor: style.backgroundColor || '#fbbf24',
                padding: `${style.backgroundSpread || 0}px`,
                borderRadius: `${style.backgroundCornerRadius || 0}px`
            };
        default:
            return {};
    }
};



// --- LayerRenderer Component ---

export const LayerRenderer = ({
    layer,
    selectedId,
    selectedIds,
    onSelect,
    onSelectMulti,
    onDragStart,
    layerPositions,
    isDragging,
    justDraggedRef,
    textContents,
    onTextContentChange,
    editingTextId,
    onTextDoubleClick,
    textStyles,
    onTextEditComplete,
    backgroundSettings,
    imageSettings,
    shapeSettings,
    scrollerSettings,
    widgetSettings,
    videoSettings,
    hoveredId,
    onHoverChange,
    onImageLoad,
    currentTime = 0,
    isPlaying = false,
    isTimelineOpen = false,
    isInsideScroller = false,
    lastClickRef,
    rotatingLayer,
    resizingLayer,
    readOnly = false,
    canvasScale = 1
}: {
    layer: Layer;
    selectedId?: string | null;
    selectedIds?: string[];
    onSelect?: (id: string | null) => void;
    onSelectMulti?: (id: string | null, shiftKey: boolean) => void;
    onDragStart?: (id: string, e: React.MouseEvent) => void;
    layerPositions?: Record<string, { left: number; top: number; width?: number; height?: number; rotation?: number }>;
    isDragging?: boolean;
    justDraggedRef?: React.RefObject<boolean>;
    textContents?: Record<string, string>;
    onTextContentChange?: (id: string, content: string) => void;
    editingTextId?: string | null;
    onTextDoubleClick?: (id: string) => void;
    textStyles?: Record<string, Partial<TextStyle>>;
    onTextEditComplete?: (id: string) => void;
    backgroundSettings?: Record<string, BackgroundSettings>;
    imageSettings?: Record<string, ImageSettings>;
    shapeSettings?: Record<string, ShapeSettings>;
    scrollerSettings?: Record<string, any>;
    widgetSettings?: Record<string, WidgetSettings>;
    videoSettings?: Record<string, VideoSettings>;
    hoveredId?: string | null;
    onHoverChange?: (id: string | null) => void;
    onImageLoad?: () => void;
    currentTime?: number;
    isPlaying?: boolean;
    isTimelineOpen?: boolean;
    isInsideScroller?: boolean;
    lastClickRef?: React.MutableRefObject<{ time: number, id: string | null } | null>;
    rotatingLayer?: { id: string } | null;
    resizingLayer?: { id: string } | null;
    readOnly?: boolean;
    canvasScale?: number;
}) => {
    const isSelected = !readOnly && (selectedIds ? selectedIds.includes(layer.id) : selectedId === layer.id);
    const isBeingDragged = !readOnly && isDragging && isSelected;
    const isLocked = layer.locked;
    const isDraggable = !readOnly && (layer.type !== 'frame' || (layer.className && layer.className.includes('cursor-move'))) && !isLocked;
    const isHovered = !readOnly && hoveredId === layer.id;
    const isEditing = !readOnly && editingTextId === layer.id;
    const editRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isEditing && editRef.current) {
            const textContent = textContents?.[layer.id] ?? layer.content ?? '';
            editRef.current.innerText = textContent;
            editRef.current.focus();
            try {
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(editRef.current);
                range.collapse(false); // Collapse to end
                sel?.removeAllRanges();
                sel?.addRange(range);
            } catch (e) {
                console.error('Failed to focus/move caret:', e);
            }
        }
    }, [isEditing]);

    // Keyframe Interpolation
    const layerAnimation = useMemo(() => {
        let anim = layer.animation;
        if (layer.type === 'text' && textStyles?.[layer.id]?.animation?.type) {
            const s = textStyles[layer.id];
            anim = s.animation as any;
        } else if (layer.type === 'image' && imageSettings?.[layer.id]?.animation?.type) {
            const s = imageSettings[layer.id];
            anim = s.animation as any;
        } else if (layer.type === 'video' && videoSettings?.[layer.id]?.animation) {
            const s = videoSettings[layer.id].animation;
            if (s) {
                anim = {
                    type: (s.type as any) || 'none',
                    duration: s.duration || 1000,
                    delay: s.delay || 0,
                    easing: (s.easing as any) || 'ease',
                    loop: s.loop,
                    keyframes: s.keyframes
                };
            }
        }
        return anim;
    }, [layer, textStyles, imageSettings, shapeSettings, videoSettings]);

    const interpolated = useInterpolatedAnimation(currentTime || 0, layerAnimation, isPlaying);

    if (layer.hidden) return null;
    const isTimelineActive = isPlaying || isTimelineOpen;
    const translationScale = canvasScale || 1;

    const selectionStyle = isHovered && !isSelected && !isBeingDragged && layer.id !== 'root' ? {
        outline: '1px solid #30A84B',
        outlineOffset: '2px'
    } : {};

    const currentPos = layerPositions ? layerPositions[layer.id] : undefined;
    const isRotatingThisLayer = !readOnly && rotatingLayer && rotatingLayer.id === layer.id;

    const currentRotation = (isTimelineActive && interpolated.rotation !== undefined)
        ? interpolated.rotation
        : (currentPos?.rotation !== undefined ? currentPos.rotation : 0);

    const isActivelyInteracting = !readOnly && (isBeingDragged || isRotatingThisLayer || (resizingLayer && resizingLayer.id === layer.id));

    const positionStyle = {
        ...layer.style,
        ...(currentPos ? {
            left: `${currentPos.left}px`,
            top: `${currentPos.top}px`,
            ...(currentPos.width !== undefined ? { width: `${currentPos.width}px` } : {}),
            ...(currentPos.height !== undefined ? { height: `${currentPos.height}px` } : {}),
            transformOrigin: 'center center',
            transition: (!readOnly && rotatingLayer && rotatingLayer.id === layer.id) ? 'none' : undefined,
        } : {
            transform: layer.style?.transform,
        })
    };

    const interactionStyle: React.CSSProperties = {
        ...positionStyle,
        ...selectionStyle,
        position: layer.id === 'root' ? 'relative' : 'absolute',
        width: layer.id === 'root' ? '100%' : positionStyle.width,
        height: layer.id === 'root' ? '100%' : positionStyle.height,
        opacity: isTimelineActive && interpolated.opacity !== undefined && !isNaN(Number(interpolated.opacity))
            ? Number(interpolated.opacity)
            : (isBeingDragged ? 0.9 : (layer.opacity !== undefined && !isNaN(Number(layer.opacity)) ? Number(layer.opacity) / 100 : 1)),
        cursor: isDraggable ? (isBeingDragged ? 'grabbing' : 'grab') : 'default',
        zIndex: isBeingDragged ? 9999 : (layer.style?.zIndex ?? (layer.id === 'root' ? 0 : 20)),
        transition: isActivelyInteracting || isTimelineActive ? 'none' : 'opacity 0.15s ease',
        pointerEvents: readOnly ? 'none' : (isInsideScroller ? 'none' : (layer.id === 'root' ? 'none' : 'auto')),
        userSelect: isEditing ? 'text' : 'none',
    };

    const getAnimationStyles = (
        type: string | undefined,
        settings: { duration?: number; delay?: number; easing?: string; loop?: boolean; keyframes?: any[] } | undefined
    ) => {
        if (!type || type === 'none') return {};
        if (isTimelineActive) return {};

        const duration = settings?.duration || 1000;
        const delay = settings?.delay || 0;
        const easing = settings?.easing || 'ease';
        const iteration = settings?.loop ? 'infinite' : '1';
        const playState = isPlaying ? 'running' : 'paused';

        return {
            '--animation-duration': `${duration}ms`,
            '--animation-delay': `${delay}ms`,
            '--animation-easing': easing,
            '--animation-iteration': iteration,
            animationPlayState: playState
        } as React.CSSProperties;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (readOnly) return;
        e.stopPropagation();
        if (onHoverChange) onHoverChange(null);

        const now = Date.now();
        if (lastClickRef) lastClickRef.current = { time: now, id: layer.id };

        if (!isEditing && !justDraggedRef?.current) {
            if (onSelectMulti) {
                onSelectMulti(layer.id, e.shiftKey);
            } else if (onSelect && (!isSelected || !e.shiftKey)) {
                onSelect(layer.id);
            }
        }

        if (isDraggable && onDragStart) onDragStart(layer.id, e);
    };

    const handleClick = (e: React.MouseEvent) => {
        if (readOnly) return;
        e.stopPropagation();
    };

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (readOnly) return;
        e.stopPropagation();
        if (!isSelected && !isBeingDragged && onHoverChange) onHoverChange(layer.id);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
        if (readOnly) return;
        e.stopPropagation();
        if (onHoverChange && hoveredId === layer.id) onHoverChange(null);
    };

    const applyBackgroundSettings = (settings: BackgroundSettings): React.CSSProperties => {
        const bgStyle: React.CSSProperties = {};
        if (settings.type === 'image' && settings.imageUrl) {
            bgStyle.backgroundImage = `url(${settings.imageUrl})`;
            bgStyle.backgroundSize = settings.imageFit;
            bgStyle.backgroundPosition = 'center';
            bgStyle.backgroundRepeat = 'no-repeat';
        } else if (settings.type === 'gradient') {
            bgStyle.background = `linear-gradient(${settings.gradientAngle}deg, ${settings.gradientStart}, ${settings.gradientEnd})`;
        } else {
            bgStyle.backgroundColor = settings.solidColor;
        }

        const originalOpacity = Number(interactionStyle.opacity) || 1;
        const settingsOpacity = !isNaN(Number(settings.opacity)) ? Number(settings.opacity) / 100 : 1;
        bgStyle.opacity = settingsOpacity * originalOpacity;

        if (settings.blur > 0) bgStyle.filter = `blur(${settings.blur}px)`;
        if (settings.overlayOpacity > 0) {
            const overlayAlpha = Math.round((settings.overlayOpacity / 100) * 255).toString(16).padStart(2, '0');
            bgStyle.boxShadow = `inset 0 0 0 1000px ${settings.overlayColor}${overlayAlpha}`;
        }
        return bgStyle;
    };

    const sanitizeClassName = (className: string) => {
        if (!readOnly || !className) return className;
        // Strip hover and layout classes that conflict with individual element positioning in the state
        return className
            .split(' ')
            .filter(c =>
                !c.startsWith('hover:') &&
                !c.startsWith('group-hover:') &&
                !c.startsWith('cursor-') &&
                !c.startsWith('w-') &&
                !c.startsWith('h-') &&
                !c.startsWith('left-') &&
                !c.startsWith('top-') &&
                !c.startsWith('right-') &&
                !c.startsWith('bottom-') &&
                !c.startsWith('inset-') &&
                !c.startsWith('min-') &&
                !c.startsWith('max-') &&
                c !== 'absolute' &&
                c !== 'relative' &&
                c !== 'fixed' &&
                c !== 'sticky'
            )
            .join(' ');
    };

    const styleWithBg = backgroundSettings && backgroundSettings[layer.id]
        ? { ...interactionStyle, ...applyBackgroundSettings(backgroundSettings[layer.id]) }
        : interactionStyle;

    // --- Generic App (Widget) Support ---
    if ((layer.type === 'app' || layer.isApp) && widgetSettings?.[layer.id]) {
        const settings = widgetSettings[layer.id];
        return (
            <motion.div
                id={layer.id}
                initial={false}
                style={{
                    ...styleWithBg,
                    overflow: 'hidden',
                    position: 'absolute',
                    //... (simplified for breviry in instructions, but I will provide the full replacement)
                    opacity: isTimelineActive ? (interpolated.opacity ?? (layer.opacity ? layer.opacity / 100 : 1)) : (layer.opacity ? layer.opacity / 100 : 1),
                    x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0,
                    y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
                    scale: isTimelineActive ? (interpolated.scale ?? 1) : 1,
                    rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
                    transform: isTimelineActive ? undefined : styleWithBg.transform,
                    filter: isTimelineActive && interpolated.blurRadius !== undefined ? `blur(${interpolated.blurRadius}px)` : (styleWithBg.filter || undefined),
                }}
                className={settings.isTransparent
                    ? sanitizeClassName(layer.className || '').split(' ').filter(c => !c.startsWith('border') && !c.startsWith('shadow')).join(' ').trim()
                    : sanitizeClassName(layer.className || '')}
                animate={isTimelineActive ? false : { opacity: isBeingDragged ? 0.9 : (layer.opacity ? layer.opacity / 100 : 1), rotate: currentRotation }}
                transition={{ duration: isActivelyInteracting ? 0 : 0.1, ease: "linear" }}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <IFrameComponent
                    key={getWidgetInteractionKey(layer.id, {
                        isDragging: isBeingDragged,
                        isResizing: resizingLayer?.id === layer.id,
                        isRotating: rotatingLayer?.id === layer.id,
                        width: currentPos?.width,
                        height: currentPos?.height,
                        left: currentPos?.left,
                        top: currentPos?.top,
                    })}
                    {...settings}
                />

                {!readOnly && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            cursor: 'grab',
                            background: 'transparent',
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />
                )}
            </motion.div>
        );
    }

    // --- Scroller App ---
    if (layer.type === 'scroller' || (layer.isApp && (layer.name?.toLowerCase().includes('scroller') || layer.id?.toLowerCase().includes('scroller')))) {
        const settings = scrollerSettings?.[layer.id] || { ...DEFAULT_SCROLLER_SETTINGS };
        const width = parseFloat(String(styleWithBg.width).replace('px', '')) || 300;
        const height = parseFloat(String(styleWithBg.height).replace('px', '')) || 60;

        return (
            <motion.div
                id={layer.id}
                initial={false}
                style={{
                    ...styleWithBg,
                    opacity: isTimelineActive ? (interpolated.opacity ?? (layer.opacity ? layer.opacity / 100 : 1)) : (layer.opacity ? layer.opacity / 100 : 1),
                    x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0,
                    y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
                    scale: isTimelineActive ? (interpolated.scale ?? 1) : 1,
                    rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
                    transform: isTimelineActive ? undefined : styleWithBg.transform,
                    filter: isTimelineActive && interpolated.blurRadius !== undefined ? `blur(${interpolated.blurRadius}px)` : (styleWithBg.filter || undefined),
                }}
                className={sanitizeClassName(layer.className || '')}
                animate={isTimelineActive ? false : { opacity: isBeingDragged ? 0.9 : (layer.opacity ? layer.opacity / 100 : 1), rotate: currentRotation }}
                transition={{ duration: isActivelyInteracting ? 0 : 0.1, ease: "linear" }}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Scroller {...settings} width={width} height={height} currentTime={currentTime} isPlaying={isPlaying} />
            </motion.div>
        );
    }

    // --- Playlist Support ---
    if (layer.type === 'playlist') {
        return (
            <PlaylistRenderer
                layer={layer}
                isPlaying={isPlaying}
                isTimelineOpen={isTimelineOpen}
                currentTime={currentTime}
                style={styleWithBg}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onImageLoad={onImageLoad}
            />
        );
    }

    // --- Shape Elements ---
    if (layer.content && typeof layer.content === 'string' && ['rectangle', 'shape', 'icon', 'line', 'arrow'].includes(layer.type)) {
        const content = layer.content;
        const shapeSetting = shapeSettings?.[layer.id] || {
            fill: layer.fill || { type: 'solid', enabled: true, color: '#1e293b' },
            stroke: layer.stroke || { enabled: false, color: '#000000', width: 2, style: 'solid' },
            cornerRadius: layer.cornerRadius ?? 0,
            opacity: layer.opacity ?? 1,
            blur: layer.blur ?? 0,
            animation: layer.animation || { type: 'none', duration: 1000, delay: 0, easing: 'ease' }
        } as any;

        const fillSettings = shapeSetting.fill || { type: 'solid', color: '#1e293b', enabled: true };
        const isGradient = fillSettings.type === 'gradient';
        const gradientId = `grad-${layer.id}`;

        // Fill logic: Default to enabled true if missing, or use actual value
        const isFillEnabled = fillSettings.enabled !== false;
        const color = isFillEnabled
            ? (fillSettings.type === 'solid' ? (fillSettings.color || '#1e293b') : `url(#${gradientId})`)
            : 'transparent';

        const strokeColor = shapeSetting.stroke?.enabled ? shapeSetting.stroke.color : 'transparent';
        const strokeWidth = shapeSetting.stroke?.enabled ? shapeSetting.stroke.width : 0;
        const strokeDash = shapeSetting.stroke?.style === 'dashed' ? '4 2' : shapeSetting.stroke?.style === 'dotted' ? '1 1' : 'none';

        const shapeAnimationClass = (isPlaying && shapeSetting?.animation?.type && shapeSetting.animation.type !== 'none')
            ? `shape-animation-${shapeSetting.animation.type}`
            : '';

        const shapeAnimationStyle = getAnimationStyles(shapeSetting?.animation?.type, shapeSetting?.animation as any);
        const shapeStyle: React.CSSProperties = {
            ...interactionStyle,
            ...shapeAnimationStyle,
            opacity: (!isNaN(Number(shapeSetting.opacity)) ? Number(shapeSetting.opacity) : 1) * (Number(interactionStyle.opacity) || 1),
            borderRadius: content === 'circle' ? '50%' : `${shapeSetting.cornerRadius ?? 0}px`,
            // BACKGROUND & BORDER logic: For primitives, apply directly to the div for perfect rendering
            backgroundColor: (content === 'rectangle' || content === 'circle') ? color : 'transparent',
            boxSizing: 'border-box',
            ...((content === 'rectangle' || content === 'circle') && shapeSetting.stroke?.enabled ? {
                border: `${shapeSetting.stroke.width}px ${shapeSetting.stroke.style || 'solid'} ${shapeSetting.stroke.color}`
            } : {}),
            overflow: (shapeSetting.stroke?.enabled || shapeSetting.blur > 0) ? 'visible' : 'hidden',
            filter: shapeSetting.blur > 0 ? `blur(${shapeSetting.blur}px)` : 'none',
        };

        const renderShape = (path: React.ReactNode, viewBox = "0 0 100 100") => (
            <motion.div
                key={`${layer.id}-${isTimelineActive}-${isPlaying}-${canvasScale}`}
                id={layer.id}
                initial={false}
                className={`${sanitizeClassName(layer.className || '')} group/shape relative transition-none`.trim()}
                style={{
                    ...shapeStyle,
                    display: 'block',
                    opacity: isTimelineActive ? (interpolated.opacity ?? (shapeSetting.opacity ?? 1)) : (shapeSetting.opacity ?? 1),
                    x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0,
                    y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
                    scale: isTimelineActive ? (interpolated.scale ?? 1) : 1,
                    rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
                    transform: isTimelineActive ? undefined : shapeStyle.transform,
                    filter: isTimelineActive && interpolated.blurRadius !== undefined ? `blur(${interpolated.blurRadius}px)` : (shapeStyle.filter || undefined),
                    borderRadius: content === 'circle' ? '50%' : shapeStyle.borderRadius,
                    animation: 'none',
                }}
                animate={isTimelineActive ? false : { opacity: isBeingDragged ? 0.9 : (shapeSetting.opacity ?? 1), rotate: currentRotation }}
                transition={{ duration: (isActivelyInteracting || isTimelineActive) ? 0 : 0.1, ease: "linear" }}
                onClick={handleClick} onMouseDown={handleMouseDown} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
            >
                {/* Animation Wrapper */}
                <div
                    className={`${shapeAnimationClass} w-full h-full relative`.trim()}
                    style={shapeAnimationStyle}
                >
                    {/* SVG Layer (for actual shape path) */}
                    {true && ( // Render all shapes as SVGs for non-scaling-stroke support
                        <svg width="100%" height="100%" viewBox={content === 'rectangle' ? "0 0 100 100" : viewBox} fill="none" preserveAspectRatio={['line', 'arrow'].includes(content) ? 'none' : (['zigzag', 'wavy', 'double', 'curved'].includes(content) ? 'xMidYMid meet' : 'none')} style={{ display: 'block', overflow: 'visible' }}>
                            {isGradient && (
                                <defs>
                                    <linearGradient
                                        id={gradientId}
                                        gradientTransform={`rotate(${fillSettings.gradientAngle || 0}, 0.5, 0.5)`}
                                        x1="0%" y1="0%" x2="0%" y2="100%"
                                    >
                                        <stop offset="0%" stopColor={fillSettings.gradientStart || '#000000'} />
                                        <stop offset="100%" stopColor={fillSettings.gradientEnd || '#ffffff'} />
                                    </linearGradient>
                                </defs>
                            )}
                            {React.Children.map(path, child => {
                                if (!React.isValidElement(child)) return child;

                                const isStrokeOnly = ['line', 'arrow', 'zigzag', 'wavy', 'double', 'curved', 'check-circle', 'alert-triangle', 'user', 'shopping-cart', 'mail', 'map-pin', 'search', 'calendar', 'home', 'settings', 'trash', 'edit', 'download', 'share', 'link', 'image'].includes(content);

                                return React.cloneElement(child as React.ReactElement, {
                                    fill: (content === 'rectangle' || content === 'circle') ? 'none' : (isStrokeOnly ? 'none' : (child.props.fill === 'none' ? 'none' : color)),
                                    stroke: (content === 'rectangle' || content === 'circle') ? 'none' : (isStrokeOnly ? color : (child.props.stroke === 'none' ? 'none' : strokeColor)),
                                    strokeWidth: isStrokeOnly ? (shapeSetting.stroke?.width || 2) : strokeWidth,
                                    strokeDasharray: strokeDash,
                                    vectorEffect: 'non-scaling-stroke',
                                    strokeLinecap: 'round',
                                    strokeLinejoin: 'round'
                                } as any);
                            })}
                        </svg>
                    )}
                </div>
                {layer.children && layer.children.length > 0 && (
                    <div style={{ position: 'absolute', inset: 0, borderRadius: content === 'circle' ? '50%' : shapeStyle.borderRadius, overflow: 'hidden', pointerEvents: readOnly ? 'none' : 'none', zIndex: 10 }}>
                        <div style={{ pointerEvents: readOnly ? 'none' : 'auto', width: '100%', height: '100%', position: 'relative' }}>
                            {layer.children.map((child, idx) => (
                                <LayerRenderer
                                    key={`layer-${layer.id}-child-${child.id || idx}`}
                                    layer={child}
                                    {...{
                                        selectedId, selectedIds, onSelect, onSelectMulti, onDragStart, layerPositions, isDragging, justDraggedRef, textContents, onTextContentChange, editingTextId, onTextDoubleClick, textStyles, onTextEditComplete, backgroundSettings, imageSettings, shapeSettings, scrollerSettings, widgetSettings, videoSettings, hoveredId, onHoverChange, onImageLoad, currentTime, isPlaying, isTimelineOpen, isInsideScroller, lastClickRef, rotatingLayer, resizingLayer, readOnly
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </motion.div>
        );

        const shapes: Record<string, { path: React.ReactNode, viewBox?: string }> = {
            rectangle: { path: <rect x="0" y="0" width="100" height="100" /> },
            circle: { path: <circle cx="50" cy="50" r="50" /> },
            triangle: { path: <path d="M50 0L100 100H0L50 0Z" /> },
            rhombus: { path: <path d="M50 0L100 50L50 100L0 50L50 0Z" /> },
            trapezoid: { path: <path d="M20 0H80L100 100H0L20 0Z" /> },
            parallelogram: { path: <path d="M25 0H100L75 100H0L25 0Z" /> },
            pentagon: { path: <path d="M50 0L100 38L81 100H19L0 38L50 0Z" /> },
            hexagon: { path: <path d="M25 0H75L100 50L75 100H25L0 50L25 0Z" /> },
            octagon: { path: <path d="M30 0H70L100 30V70L70 100H30L0 70V30L30 0Z" /> },
            star: { path: <path d="M50 0L61 35L98 35L68 57L79 91L50 70L21 91L32 57L2 35L39 35L50 0Z" /> },
            heart: { path: <path d="M50 90C 20 60 0 30 20 15C 35 5 50 15 50 15C 50 15 65 5 80 15C 100 30 80 60 50 90Z" /> },
            'chat-bubble': { path: <path d="M10 10H90V65H45L20 85V65H10V10Z" /> },
            bell: { path: <path d="M50 5C40 5 33 12 33 22V30C23 35 18 45 18 55V75H12V80H88V75H82V55C82 45 77 35 67 30V22C67 12 60 5 50 5ZM40 85C40 90 45 95 50 95C55 95 60 90 60 85H40Z" /> },
            info: { path: <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />, viewBox: "0 0 24 24" },
            help: { path: <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />, viewBox: "0 0 24 24" },
            'check-circle': { path: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>, viewBox: "0 0 24 24" },
            'alert-triangle': { path: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>, viewBox: "0 0 24 24" },
            line: { path: <path d="M0 50H100" />, viewBox: "0 0 100 100" },
            arrow: { path: <path d="M0 50H95M95 50L80 35M95 50L80 65" />, viewBox: "0 0 100 100" },
            // Expanded Shape Pack
            plus: { path: <path d="M14 2H18V14H30V18H18V30H14V18H2V14H14V2Z" />, viewBox: "0 0 32 32" },
            'rounded-rect': { path: <rect x="4" y="8" width="24" height="16" rx="4" />, viewBox: "0 0 32 32" },
            pill: { path: <rect x="4" y="10" width="24" height="12" rx="6" />, viewBox: "0 0 32 32" },
            'star-4': { path: <path d="M16 2L19 13L30 16L19 19L16 30L13 19L2 16L13 13L16 2Z" />, viewBox: "0 0 32 32" },
            moon: { path: <path d="M24 24.5C22.24 25.5 20.18 26 18 26C11.37 26 6 20.63 6 14C6 9.47 8.5 5.52 12.2 3.5C10.87 5 10 7 10 9.25C10 15.74 15.26 21 21.75 21C22.56 21 23.34 20.92 24 20.77C24 20.77 24 24.5 24 24.5Z" />, viewBox: "0 0 32 32" },
            lightning: { path: <path d="M21 2L10 18H18L11 30L22 14H14L21 2Z" />, viewBox: "0 0 32 32" },
            cloud: { path: <path d="M25 14C27.21 14 29 15.79 29 18C29 20.21 27.21 22 25 22H9C6.24 22 4 19.76 4 17C4 14.24 6.24 12 9 12C9.4 12 9.78 12.05 10.15 12.14C11.5 9.15 14.51 7 18 7C22.1 7 25.54 10.12 25.96 14.1C25.65 14.04 25.33 14 25 14Z" />, viewBox: "0 0 32 32" },
            shield: { path: <path d="M16 2L6 6V14C6 20.14 10.25 25.86 16 28C21.75 25.86 26 20.14 26 14V6L16 2Z" />, viewBox: "0 0 32 32" },
            flag: { path: <path d="M6 2H8V30H6V2ZM8 4L26 11L8 18V4Z" />, viewBox: "0 0 32 32" },
            'arrow-right': { path: <path d="M4 14H22L16 8L18.8 5.2L29.6 16L18.8 26.8L16 24L22 18H4V14Z" />, viewBox: "0 0 32 32" },
            'arrow-left': { path: <path d="M28 14H10L16 8L13.2 5.2L2.4 16L13.2 26.8L16 24L10 18H28V14Z" />, viewBox: "0 0 32 32" },
            'arrow-up': { path: <path d="M14 28V10L8 16L5.2 13.2L16 2.4L26.8 13.2L24 16L18 10V28H14Z" />, viewBox: "0 0 32 32" },
            'arrow-down': { path: <path d="M14 4V22L8 16L5.2 18.8L16 29.6L26.8 18.8L24 16L18 22V4H14Z" />, viewBox: "0 0 32 32" },
            'chevron-right': { path: <path d="M10 6L20 16L10 26L13 29L26 16L13 3L10 6Z" />, viewBox: "0 0 32 32" },
            sun: { path: <path d="M16 10C12.69 10 10 12.69 10 16C10 19.31 12.69 22 16 22C19.31 22 22 19.31 22 16C22 12.69 19.31 10 16 10ZM15 2H17V6H15V2ZM15 26H17V30H15V26ZM26 15H30V17H26V15ZM2 15H6V17H2V15ZM23.07 7.51L24.49 8.93L21.66 11.76L20.24 10.34L23.07 7.51ZM8.93 23.07L10.35 24.49L7.52 27.32L6.1 25.9L8.93 23.07ZM23.07 24.49L21.65 23.07L24.48 20.24L25.9 21.66L23.07 24.49ZM8.93 8.93L7.51 7.51L4.68 10.34L6.1 11.76L8.93 8.93Z" />, viewBox: "0 0 32 32" },
            drop: { path: <path d="M16 2C16 2 6 12 6 19C6 24.52 10.48 29 16 29C21.52 29 26 24.52 26 19C26 12 16 2 16 2" />, viewBox: "0 0 32 32" },
            leaf: { path: <path d="M26 2C18 2 10 10 4 28C22 22 30 14 30 6C30 6 28 2 26 2Z" />, viewBox: "0 0 32 32" },
            cross: { path: <path d="M26 8.8L23.2 6L16 13.2L8.8 6L6 8.8L13.2 16L6 23.2L8.8 26L16 18.8L23.2 26L26 23.2L18.8 16L26 8.8Z" />, viewBox: "0 0 32 32" },
            wave: { path: <path d="M2 16C6 12 10 12 14 16C18 20 22 20 26 16C28 14 30 14 30 14V18C30 18 28 18 26 20C22 24 18 24 14 20C10 16 6 16 2 20V16Z" />, viewBox: "0 0 32 32" },
            'semi-circle': { path: <path d="M4 16C4 9.37 9.37 4 16 4C22.63 4 28 9.37 28 16H4Z" />, viewBox: "0 0 32 32" },
            ring: { path: <path fillRule="evenodd" clipRule="evenodd" d="M16 28C22.6274 28 28 22.6274 28 16C28 9.37258 22.6274 4 16 4C9.37258 4 4 9.37258 4 16C4 22.6274 9.37258 28 16 28ZM16 22C19.3137 22 22 19.3137 22 16C22 12.6863 19.3137 10 16 10C12.6863 10 10 12.6863 10 16C10 19.3137 12.6863 22 16 22Z" />, viewBox: "0 0 32 32" },
            bookmark: { path: <path d="M6 4V28L16 22L26 28V4H6Z" />, viewBox: "0 0 32 32" },
            tag: { path: <path d="M4 14V4H14L28 18L18 28L4 14ZM9 9C10.1 9 11 8.1 11 7C11 5.9 10.1 5 9 5C7.9 5 7 5.9 7 7C7 8.1 7.9 9 9 9Z" />, viewBox: "0 0 32 32" },
            award: { path: <path d="M16 2C10.48 2 6 6.48 6 12C6 15.31 7.62 18.23 10.1 20.06L8 30L16 26L24 30L21.9 20.06C24.38 18.23 26 15.31 26 12C26 6.48 21.52 2 16 2ZM16 18C12.69 18 10 15.31 10 12C10 8.69 12.69 6 16 6C19.31 6 22 8.69 22 12C22 15.31 19.31 18 16 18Z" />, viewBox: "0 0 32 32" },
            puzzle: { path: <path d="M22 14V8H18C18 5.79 16.21 4 14 4S10 5.79 10 8H6V14C8.21 14 10 15.79 10 18S8.21 22 6 22V28H12V24C12 21.79 13.79 20 16 20S20 21.79 20 24V28H26V22H22C19.79 22 18 20.21 18 18S19.79 14 22 14Z" />, viewBox: "0 0 32 32" },
            infinity: { path: <path d="M22 10.5C19.5 10.5 17.5 12.5 16 14.5C14.5 12.5 12.5 10.5 10 10.5C6.5 10.5 4 13 4 16.5C4 20 6.5 22.5 10 22.5C12.5 22.5 14.5 20.5 16 18.5C17.5 20.5 19.5 22.5 22 22.5C25.5 22.5 28 20 28 16.5C28 13 25.5 10.5 22 10.5ZM10 19.5C8.3 19.5 7 18.2 7 16.5C7 14.8 8.3 13.5 10 13.5C11.4 13.5 12.8 14.8 14.1 16.5C12.8 18.2 11.4 19.5 10 19.5ZM22 19.5C20.6 19.5 19.2 18.2 17.9 16.5C19.2 14.8 20.6 13.5 22 13.5C23.7 13.5 25 14.8 25 16.5C25 18.2 23.7 19.5 22 19.5Z" />, viewBox: "0 0 32 32" },
            flower: { path: <path d="M16 16C16 16 16 6 11 6C6 6 6 11 6 11C6 11 16 11 16 16ZM16 16C16 16 26 16 26 11C26 6 21 6 21 6C21 6 21 16 16 16ZM16 16C16 16 16 26 21 26C26 26 26 21 26 21C26 21 16 21 16 16ZM16 16C16 16 6 16 6 21C6 26 11 26 11 26C11 26 11 16 16 16Z" />, viewBox: "0 0 32 32" },
            // Badges
            starburst: { path: <path d="M30 16L27.5 12.5L30 9L26 8L26 4L22 6L19 3L16 5.5L13 3L10 6L6 4L6 8L2 9L4.5 12.5L2 16L4.5 19.5L2 23L6 24L6 28L10 26L13 29L16 26.5L19 29L22 26L26 28L26 24L30 23L27.5 19.5L30 16Z" />, viewBox: "0 0 32 32" },
            badge: { path: <path d="M16 2C8.27 2 2 8.27 2 16C2 23.73 8.27 30 16 30C23.73 30 30 23.73 30 16C30 8.27 23.73 2 16 2ZM16 26C10.48 26 6 21.52 6 16C6 10.48 10.48 6 16 6C21.52 6 26 10.48 26 16C26 21.52 21.52 26 16 26Z" fillRule="evenodd" />, viewBox: "0 0 32 32" },
            ribbon: { path: <path d="M4 4H28V22L16 28L4 22V4Z" />, viewBox: "0 0 32 32" },
            seal: { path: <path d="M16 2L18.5 4.5L22 3.5L23 7L26.5 7.5L26 11L29 12.5L27 15.5L29 18.5L26 20L26.5 23.5L23 24L22 27.5L18.5 26.5L16 29L13.5 26.5L10 27.5L9 24L5.5 23.5L6 20L3 18.5L5 15.5L3 12.5L6 11L5.5 7.5L9 7L10 3.5L13.5 4.5L16 2Z" />, viewBox: "0 0 32 32" },
            banner: { path: <path d="M2 6H30V20L16 26L2 20V6Z" />, viewBox: "0 0 32 32" },
            // Extra Lines
            zigzag: { path: <path d="M2 16L6 10L12 22L18 10L24 22L30 16" />, viewBox: "0 0 32 32" },
            wavy: { path: <path d="M2 16C6 10 10 10 14 16C18 22 22 22 26 16" />, viewBox: "0 0 32 32" },
            double: { path: <path d="M8 10L2 16L8 22M2 16H30M24 10L30 16L24 22" />, viewBox: "0 0 32 32" },
            curved: { path: <path d="M4 22Q16 22 16 12L16 4M10 10L16 4L22 10" />, viewBox: "0 0 32 32" },
            // UI Icons
            check: { path: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>, viewBox: "0 0 24 24" },
            alert: { path: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>, viewBox: "0 0 24 24" },
            user: { path: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>, viewBox: "0 0 24 24" },
            'shopping-cart': { path: <><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></>, viewBox: "0 0 24 24" },
            mail: { path: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>, viewBox: "0 0 24 24" },
            'map-pin': { path: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>, viewBox: "0 0 24 24" },
            search: { path: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>, viewBox: "0 0 24 24" },
            calendar: { path: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>, viewBox: "0 0 24 24" },
            home: { path: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>, viewBox: "0 0 24 24" },
            settings: { path: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></>, viewBox: "0 0 24 24" },
            trash: { path: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>, viewBox: "0 0 24 24" },
            edit: { path: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>, viewBox: "0 0 24 24" },
            download: { path: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>, viewBox: "0 0 24 24" },
            share: { path: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></>, viewBox: "0 0 24 24" },
            link: { path: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>, viewBox: "0 0 24 24" },
            image: { path: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>, viewBox: "0 0 24 24" },
        };

        if (shapes[content]) return renderShape(shapes[content].path, shapes[content].viewBox);
    }

    // --- Image Type ---
    if (layer.type === 'image') {
        const imgSettings = imageSettings?.[layer.id];
        const imageAnimationClass = (isPlaying && imgSettings?.animation?.type && imgSettings.animation.type !== 'none')
            ? `shape-animation-${imgSettings.animation.type}`
            : '';

        const imageAnimationStyle = getAnimationStyles(imgSettings?.animation?.type, imgSettings?.animation);

        const wrapperStyle: React.CSSProperties = {
            ...interactionStyle, ...imageAnimationStyle,
            opacity: (!isNaN(Number(imgSettings?.opacity)) ? Number(imgSettings!.opacity) / 100 : 1) * (Number(interactionStyle.opacity) || 1),
            borderRadius: `${imgSettings?.borderRadius ?? 0}px`,
            overflow: 'hidden',
            display: 'block',
            boxSizing: 'border-box',
            ...(imgSettings?.crop?.enabled ? {
                clipPath: `inset(${imgSettings.crop.top}% ${imgSettings.crop.right}% ${imgSettings.crop.bottom}% ${imgSettings.crop.left}%)`
            } : {}),
            ...(imgSettings?.borderWidth ? {
                border: `${imgSettings.borderWidth}px solid ${imgSettings.borderColor || '#000000'}`
            } : {})
        };
        const flipX = imgSettings?.flipHorizontal ? -1 : 1;
        const flipY = imgSettings?.flipVertical ? -1 : 1;
        const imgStyleWithFilters: React.CSSProperties = {
            width: '100%', height: '100%',
            objectFit: imgSettings?.objectFit || 'cover',
            filter: imgSettings ? `brightness(${imgSettings.brightness}%) contrast(${imgSettings.contrast}%) saturate(${imgSettings.saturation}%) blur(${imgSettings.blur}px)` : 'none',
            transform: (flipX !== 1 || flipY !== 1) ? `scaleX(${flipX}) scaleY(${flipY})` : 'none',
            transformOrigin: 'center center',
        };

        const finalSafeImageStyle = {
            ...wrapperStyle,
            opacity: isTimelineActive ? (interpolated.opacity ?? wrapperStyle.opacity) : wrapperStyle.opacity,
            x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0,
            y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
            scale: isTimelineActive ? (interpolated.scale ?? 1) : 1,
            rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
            transform: isTimelineActive ? undefined : wrapperStyle.transform,
            filter: (isTimelineActive && interpolated.blurRadius !== undefined) ? `blur(${interpolated.blurRadius}px)` : wrapperStyle.filter,
        } as React.CSSProperties;

        const animationKey = imgSettings?.animation?.type && imgSettings.animation.type !== 'none'
            ? `${layer.id}-${imgSettings.animation.type}-${imgSettings.animation.duration}-${imgSettings.animation.delay}-${imgSettings.animation.easing}`
            : `${layer.id}-static`;

        return (
            <SafeImage
                id={layer.id}
                className={sanitizeClassName(layer.className || '').replace(/\b(group-hover|hover):scale-\d+\b/g, '')}
                style={finalSafeImageStyle}
                imgStyle={imgStyleWithFilters}
                src={imgSettings?.url || layer.content || '/logo.png'}
                alt={layer.name}
                onLoad={onImageLoad}
                animationKey={animationKey}
                imageAnimationClass={imageAnimationClass}
                imageAnimationStyle={imageAnimationStyle}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />
        );
    }

    // --- Video Type ---
    if (layer.type === 'video') {
        const videoSetting = videoSettings?.[layer.id];
        const videoUrl = videoSetting?.url || layer.content;

        const videoAnimationClass = (isPlaying && videoSetting?.animation && videoSetting.animation.type !== 'none')
            ? `shape-animation-${videoSetting.animation.type}`
            : '';

        const videoAnimationStyle = getAnimationStyles(videoSetting?.animation?.type, videoSetting?.animation as any);

        const wrapperStyle: React.CSSProperties = {
            ...interactionStyle, ...videoAnimationStyle,
            opacity: (!isNaN(Number(videoSetting?.opacity)) ? Number(videoSetting!.opacity) / 100 : 1) * (Number(interactionStyle.opacity) || 1),
            borderRadius: `${videoSetting?.borderRadius ?? 0}px`,
            overflow: 'hidden',
            display: 'block',
            boxSizing: 'border-box',
            ...((videoSetting as any)?.borderWidth ? {
                border: `${(videoSetting as any).borderWidth}px solid ${(videoSetting as any).borderColor || '#000000'}`
            } : {})
        };

        const finalSafeVideoStyle: React.CSSProperties = {
            ...wrapperStyle,
            opacity: isTimelineActive ? (interpolated.opacity ?? (videoSetting?.opacity ? videoSetting.opacity / 100 : 1)) : (videoSetting?.opacity ? videoSetting.opacity / 100 : 1),
            x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0, y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
            scale: isTimelineActive ? (interpolated.scale ?? 1) : 1,
            rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
            transform: isTimelineActive ? undefined : wrapperStyle.transform,
            filter: isTimelineActive && interpolated.blurRadius !== undefined ? `blur(${interpolated.blurRadius}px)` : (wrapperStyle.filter || undefined),
            clipPath: isTimelineActive && interpolated.revealProgress !== undefined
                ? (layerAnimation?.type === 'typewriter'
                    ? `inset(0 ${100 - (Math.floor(Number(interpolated.revealProgress) / 5) * 5)}% 0 0)` // Stepped effect for typewriter
                    : `inset(0 ${100 - Number(interpolated.revealProgress)}% 0 0)`)
                : undefined,
            transition: 'none',
            animation: 'none',
        };

        return (
            <SafeVideo
                id={layer.id}
                className={(layer.className || '').replace(/\b(group-hover|hover):scale-\d+\b/g, '')}
                style={finalSafeVideoStyle}
                videoStyle={{
                    width: '100%',
                    height: '100%',
                    objectFit: videoSetting?.objectFit || 'cover',
                }}
                src={videoUrl || ''}
                autoPlay={videoSetting?.autoplay ?? true}
                loop={videoSetting?.loop ?? true}
                muted={videoSetting?.muted ?? true}
                animationClass={videoAnimationClass}
                animationStyle={videoAnimationStyle}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />
        );
    }

    // --- Text Type ---
    if (layer.type === 'text') {
        const textStyle = textStyles?.[layer.id];
        const hasBinding = !!textStyle?.binding?.apiKey;
        const textContent = textContents?.[layer.id] ?? layer.content ?? '';
        const fontWeightMap: Record<string, number> = { 'normal': 400, 'medium': 500, 'semibold': 600, 'bold': 700, 'extrabold': 800, 'black': 900 };

        const fill = textStyle?.fill;
        const isTextGradient = fill?.type === 'gradient';

        const styleOverrides: React.CSSProperties = textStyle ? {
            fontFamily: textStyle.fontFamily, fontSize: textStyle.fontSize ? `${textStyle.fontSize}px` : undefined,
            fontWeight: fontWeightMap[textStyle.fontWeight!] || 400,
            color: isTextGradient ? 'transparent' : (fill?.color || textStyle.color),
            textAlign: textStyle.textAlign, textTransform: textStyle.uppercase ? 'uppercase' : undefined,
            fontStyle: textStyle.italic ? 'italic' : undefined, textDecoration: textStyle.underline ? 'underline' : undefined,
            opacity: (textStyle.opacity !== undefined && !isNaN(Number(textStyle.opacity))) ? Number(textStyle.opacity) / 100 : undefined,
        } : {};

        const gradientStyles: React.CSSProperties = isTextGradient ? {
            backgroundImage: `linear-gradient(${fill?.gradientAngle || 0}deg, ${fill?.gradientStart || '#000000'}, ${fill?.gradientEnd || '#ffffff'})`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block' // Needed for clipping sometimes
        } : {};

        const finalStyle: React.CSSProperties = {
            ...interactionStyle, ...styleOverrides,
            width: interactionStyle.width ?? 'max-content',
            height: hasBinding ? 'auto' : (interactionStyle.height ?? 'fit-content'),
            display: 'flex', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            alignItems: textStyle?.verticalAlign === 'top' ? 'flex-start' : textStyle?.verticalAlign === 'bottom' ? 'flex-end' : 'center',
            justifyContent: textStyle?.textAlign === 'left' ? 'flex-start' : textStyle?.textAlign === 'right' ? 'flex-end' : 'center'
        };

        if (isEditing) {
            const editHeight = hasBinding 
                ? 'auto' 
                : ((currentPos?.height !== undefined) ? `${currentPos.height}px` : (interactionStyle.height || 'fit-content'));

            return (
                <div
                    id={layer.id}
                    className={`${layer.className || ''} absolute z-[10005] outline-[2px] outline-solid outline-[#30A84B] cursor-text flex items-center justify-center`.trim()}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    style={{
                        ...finalStyle,
                        width: hasBinding 
                            ? 'max-content' 
                            : (currentPos?.width ? `${currentPos.width}px` : 'max-content'),
                        height: editHeight,
                        minWidth: hasBinding ? undefined : (!currentPos?.width ? '200px' : undefined),
                        display: 'flex',
                        alignItems: textStyle?.verticalAlign === 'top' ? 'flex-start' : textStyle?.verticalAlign === 'bottom' ? 'flex-end' : 'center',
                        justifyContent: textStyle?.textAlign === 'left' ? 'flex-start' : textStyle?.textAlign === 'right' ? 'flex-end' : 'center',
                        userSelect: 'text',
                        lineHeight: '1.2'
                    }}
                >
                    <div className="w-full h-full flex items-center justify-center pointer-events-none" style={{ display: 'flex', alignItems: 'inherit', justifyContent: 'inherit' }}>
                        <div
                            ref={editRef}
                            contentEditable
                            suppressContentEditableWarning
                            onInput={e => onTextContentChange?.(layer.id, e.currentTarget.innerText)}
                            onBlur={() => { onTextDoubleClick?.(''); onTextEditComplete?.(layer.id); }}
                            className="bg-transparent border-none p-0 m-0 outline-none focus:ring-0 active:ring-0 pointer-events-auto"
                            style={{
                                ...styleOverrides,
                                textAlign: textStyle?.textAlign as any || 'center',
                                whiteSpace: (currentPos?.width && currentPos.width > 0) ? 'pre-wrap' : 'nowrap',
                                wordBreak: 'break-word',
                                userSelect: 'text',
                                cursor: 'text',
                                minWidth: '10px',
                                lineHeight: '1',
                                padding: '0px',
                                margin: '0px',
                                display: 'inline-block' // Match static text node behavior
                            }}
                        />
                    </div>
                </div>
            );
        }

        const textAnimationKey = textStyle?.animation?.type && textStyle.animation.type !== 'none'
            ? `${layer.id}-${textStyle.animation.type}-${textStyle.animation.duration}-${textStyle.animation.delay}-${textStyle.animation.easing}`
            : `${layer.id}-static`;

        const textAnimationClass = (isPlaying && textStyle?.animation?.type && textStyle.animation.type !== 'none')
            ? `shape-animation-${textStyle.animation.type}`
            : '';

        return (
            <motion.div
                key={textAnimationKey}
                id={layer.id}
                initial={false}
                className={`${layer.className || ''} select-none`.trim()}
                style={{
                    ...finalStyle,
                    opacity: isTimelineActive ? (interpolated.opacity ?? finalStyle.opacity) : (textStyle?.opacity !== undefined ? textStyle.opacity / 100 : undefined),
                    x: isTimelineActive ? (interpolated.x ?? 0) * translationScale : 0, y: isTimelineActive ? (interpolated.y ?? 0) * translationScale : 0,
                    scale: isTimelineActive ? (interpolated.scale ?? 1) : 1, rotate: isTimelineActive ? (interpolated.rotation ?? currentRotation) : currentRotation,
                    width: (isTimelineActive && interpolated.width !== undefined) 
                        ? interpolated.width 
                        : (currentPos?.width ? `${currentPos.width}px` : (layer.style?.width ? layer.style.width : 'fit-content')),
                    height: (hasBinding)
                        ? 'auto'
                        : (currentPos?.height ? `${currentPos.height}px` : (layer.style?.height ? layer.style.height : 'fit-content')),
                    filter: isTimelineActive && interpolated.blurRadius !== undefined ? `blur(${interpolated.blurRadius}px)` : (finalStyle.filter || undefined),
                    clipPath: isTimelineActive && interpolated.revealProgress !== undefined
                        ? (layerAnimation?.type === 'typewriter'
                            ? `inset(0 ${100 - (Math.floor(Number(interpolated.revealProgress) / 5) * 5)}% 0 0)` // Stepped effect for typewriter
                            : `inset(0 ${100 - Number(interpolated.revealProgress)}% 0 0)`)
                        : undefined,
                    animation: 'none',
                }}
                animate={isTimelineActive ? false : { opacity: isBeingDragged ? 0.9 : (textStyle?.opacity !== undefined ? textStyle.opacity / 100 : 1), rotate: currentRotation }}
                transition={{ duration: (isActivelyInteracting || isTimelineActive) ? 0 : 0.1, ease: "linear" }}
                onClickCapture={handleClick} onDoubleClick={e => { if (readOnly) return; e.stopPropagation(); onTextDoubleClick?.(layer.id); }} onMouseDown={handleMouseDown} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
            >
                {/* Animation Wrapper matching Editor structure */}
                <div
                    className={`${textAnimationClass} w-full ${hasBinding ? 'h-auto' : 'h-full'} flex`.trim()}
                    style={{
                        ...getAnimationStyles(textStyle?.animation?.type, textStyle?.animation as any),
                        alignItems: textStyle?.verticalAlign === 'top' ? 'flex-start' : textStyle?.verticalAlign === 'bottom' ? 'flex-end' : 'center',
                        justifyContent: textStyle?.textAlign === 'left' ? 'flex-start' : textStyle?.textAlign === 'right' ? 'flex-end' : 'center'
                    }}
                >
                    <div
                        data-text-node="true"
                        style={{
                            fontSize: textStyle?.fontSize ? `${textStyle.fontSize}px` : '16px',
                            textAlign: textStyle?.textAlign as any || 'center',
                            lineHeight: textStyle?.lineHeight ?? '1.2',
                            letterSpacing: textStyle?.letterSpacing ? `${textStyle.letterSpacing}px` : 'normal',
                            width: '100%',
                            display: 'inline-block',
                            padding: '0px',
                            margin: '0px',
                            whiteSpace: 'inherit',
                            wordBreak: 'inherit',
                            pointerEvents: 'none',
                            ...gradientStyles,
                            ...getTextEffectStyles(textStyle)
                        }}
                    >
                        {textContent}
                    </div>
                </div>
            </motion.div>
        );
    }

    // --- Default Recursive Rendering (Frames/Groups) ---
    return (
        <div id={layer.id} style={styleWithBg} className={layer.className} onClick={handleClick} onMouseDown={handleMouseDown} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {layer.children?.map((child, idx) => (
                <LayerRenderer
                    key={`layer-${layer.id}-child-${child.id || idx}-${idx}`}
                    layer={child}
                    {...{
                        selectedId, selectedIds, onSelect, onSelectMulti, onDragStart, layerPositions, isDragging, justDraggedRef, textContents, onTextContentChange, editingTextId, onTextDoubleClick, textStyles, onTextEditComplete, backgroundSettings, imageSettings, shapeSettings, scrollerSettings, widgetSettings, videoSettings, hoveredId, onHoverChange, onImageLoad, currentTime, isPlaying, isTimelineOpen, isInsideScroller, lastClickRef, rotatingLayer, resizingLayer, readOnly, canvasScale
                    }}
                />
            ))}
        </div>
    );
};
